import { hasStartSet } from '@ardoq/api-types';
import type {
  APIViewpointAttributes,
  ArdoqId,
  PersistedMetamodel,
  ViewIds,
} from '@ardoq/api-types';
import { isArdoqError } from '@ardoq/common-helpers';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { isRouteActive, type AppRouterLocation } from 'router/declarative';
import { isViewpointModeView } from 'views/environmentals/viewSettings/utils';
import { AppModules } from 'appContainer/types';
import type { APIReportAttributes } from '@ardoq/api-types';
import type { ReportNavigationState } from 'report/navigation/reportNavigation$';
import { DashboardNavigationState } from 'dashboard/navigation/types';
import type { DashboardsStreamShape } from 'streams/dashboards/types';
import type { ViewpointsRoute } from 'viewpoints/navigation/types';
import type { LoadedStatesState } from 'loadedState/loadedStateOperations';
import type { CollectionStream } from 'streams/utils/streamUtils';
import type { MetamodelNavigationState } from 'metamodel/navigation/types';
import { isSingleTraversalState } from 'appLayout/ardoqStudio/saveAsViewpoint/saveAsViewpoint$/isSingleTraversalState';
import type { InitiativePage$State } from 'transformations/initiativePage/initiativePage$';
import type { FutureStatePage$State } from 'transformations/futureStatePage/futureStatePage$';
import type { ComponentOverviewPageState } from 'appContainer/componentOverviewPage/types';
import type {
  ReportContext,
  DashboardContext,
  ViewpointBrowseContext,
  ViewpointActiveContext,
  MetamodelContext,
  InitiativeContext,
  FutureStateContext,
  ComponentContext,
  TopLevelContext,
} from './types';

export const topLevel = (module: AppModules): TopLevelContext => ({
  assetType: 'top_level',
  module,
});

export const toReportContext = (
  reportNav: ReportNavigationState,
  reports: CollectionStream<APIReportAttributes>
): ReportContext | TopLevelContext => {
  const id = reportNav.reportId;
  const name = id ? reports.byId[id]?.name : undefined;
  if (!id || !name) return topLevel(AppModules.REPORTS);
  return {
    assetType: 'report',
    module: AppModules.REPORTS,
    _id: id,
    name,
  };
};

export const toDashboardContext = (
  dashNav: DashboardNavigationState,
  dashboards: DashboardsStreamShape
): DashboardContext | TopLevelContext => {
  const id = dashNav.selectedDashboardId;
  const name = id ? dashboards.dashboardsById[id]?.name : undefined;
  if (!id || !name) return topLevel(AppModules.DASHBOARDS);
  return {
    assetType: 'dashboard',
    module: AppModules.DASHBOARDS,
    _id: id,
    name,
  };
};

export const toViewpointBrowseContext = (
  vpNav: ViewpointsRoute
): ViewpointBrowseContext | TopLevelContext => {
  if (!vpNav.viewpointId) return topLevel(AppModules.VIEWPOINTS);
  return {
    assetType: 'viewpoint_browse',
    module: AppModules.VIEWPOINTS,
    _id: vpNav.viewpointId,
  };
};

export const toWorkspaceContext = (
  vpMode: { isViewpointMode: boolean },
  loadedState: LoadedStatesState,
  traversalState: CollectionStream<APIViewpointAttributes>,
  activeView: { mainViewId: ViewIds }
): ViewpointActiveContext | TopLevelContext => {
  if (!vpMode.isViewpointMode) return topLevel(AppModules.WORKSPACES);

  // No saved viewpoint, but the user may still be in a viewpoint-mode
  // visualization (ad-hoc exploration) — surface the active view so the
  // assistant knows a view is open.
  const noViewpoint: TopLevelContext = isViewpointModeView(
    activeView.mainViewId
  )
    ? {
        assetType: 'top_level',
        module: AppModules.WORKSPACES,
        activeViewId: activeView.mainViewId,
      }
    : topLevel(AppModules.WORKSPACES);

  if (!isSingleTraversalState(loadedState.loadedStates)) return noViewpoint;

  const entry = loadedState.loadedStates[0];
  const traversalId = entry.traversalId;
  const name = traversalId ? traversalState.byId[traversalId]?.name : undefined;

  if (!traversalId || !name) return noViewpoint;

  const startComponentIds =
    entry.componentIdsAndReferences?.startSetResult ??
    (hasStartSet(entry.data.componentSelection)
      ? entry.data.componentSelection.startSet
      : []);

  return {
    assetType: 'viewpoint_active',
    module: AppModules.WORKSPACES,
    _id: traversalId,
    name,
    startComponentIds,
  };
};

export const toMetamodelContext = (
  mmNav: MetamodelNavigationState,
  metamodels: PersistedMetamodel[]
): MetamodelContext | TopLevelContext => {
  const id = mmNav.metamodelId;
  const name = id ? metamodels.find(m => m._id === id)?.name : undefined;
  if (!id || !name) return topLevel(AppModules.METAMODEL);
  return {
    assetType: 'metamodel',
    module: AppModules.METAMODEL,
    _id: id,
    name,
  };
};

const INITIATIVE_ROUTE = '/transformations/initiatives/:initiativeId';
const FUTURE_STATE_ROUTE =
  '/transformations/initiatives/:initiativeId/future-states/:futureStateId';

export type TransformationsScope =
  | { page: 'initiative'; initiativeId: ArdoqId }
  | { page: 'future_state'; initiativeId: ArdoqId; futureStateId: ArdoqId }
  | { page: 'none' };

type FutureStateScope = Extract<TransformationsScope, { page: 'future_state' }>;

const NO_SCOPE: TransformationsScope = { page: 'none' };

export const toTransformationsScope = (
  location: AppRouterLocation
): TransformationsScope => {
  if (isRouteActive(location, FUTURE_STATE_ROUTE)) {
    return {
      page: 'future_state',
      initiativeId: location.params.initiativeId,
      futureStateId: location.params.futureStateId,
    };
  }
  if (isRouteActive(location, INITIATIVE_ROUTE)) {
    return { page: 'initiative', initiativeId: location.params.initiativeId };
  }
  return NO_SCOPE;
};

export const toInitiativeContext = (
  initiativeId: ArdoqId,
  state: InitiativePage$State
): InitiativeContext | null => {
  if (state.isLoading || isArdoqError(state.initiativeResult)) return null;
  const initiative = state.initiativeResult;
  if (initiative._id !== initiativeId || !initiative.name) return null;
  return {
    assetType: 'initiative',
    module: AppModules.TRANSFORMATIONS,
    _id: initiativeId,
    name: initiative.name,
  };
};

export const toTransformationContext = (
  initiativeId: ArdoqId,
  initiativePageState: InitiativePage$State
): InitiativeContext | TopLevelContext =>
  toInitiativeContext(initiativeId, initiativePageState) ??
  topLevel(AppModules.TRANSFORMATIONS);

export const toFutureStateContext = (
  { initiativeId, futureStateId }: FutureStateScope,
  state: FutureStatePage$State
): FutureStateContext | null => {
  if (state.status !== 'success') return null;
  const { futureState } = state;
  if (futureState._id !== futureStateId || !futureState.name) return null;
  return {
    assetType: 'future_state',
    module: AppModules.TRANSFORMATIONS,
    _id: futureStateId,
    initiativeId,
    name: futureState.name,
  };
};

export const toComponentContext = ({
  componentId,
  scopeData,
}: ComponentOverviewPageState): ComponentContext | TopLevelContext => {
  if (!componentId || !scopeData) {
    return topLevel(AppModules.COMPONENT_OVERVIEW_PAGE);
  }
  const component = enhancedScopeDataOperations.getComponent(
    scopeData,
    componentId
  );

  if (!component) return topLevel(AppModules.COMPONENT_OVERVIEW_PAGE);

  return {
    assetType: 'component',
    module: AppModules.COMPONENT_OVERVIEW_PAGE,
    _id: componentId,
    name: component.name,
  };
};
